import Cookies from 'js-cookie';
import { useState } from 'react';
import { post_RdRentalCreditCard } from '../../../services/GroupCreditCard';
import Cross from '../../../icons/Cross';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CardNumberValidate from '../../../components/CardNumberValidate';
import ExpiryDateValidator from '../../../components/ExpiryDateValidator';
import CvvValidate from '../../../components/CvvValidate';
import colorConfigs from '../../../configs/colorConfigs';
import { useTheme } from '../../../components/hooks/useTheme';
import { Button, Dialog } from '@mui/material';
import axios from 'axios';
import { ticketBaseURL } from '../../../configs/api';
import useConfirm from '../../../components/hooks/useConfirm';
import useAuth from '../../../components/hooks/useAuth';
import useTenant from '../../../components/hooks/useTenant';

type CreditCardProps = {
    showPopupAddNewCreditCard: boolean;
    setShowPopupAddNewCreditCard: (value: boolean) => void;
    setIsRefresh: (value: boolean) => void;
}

const AddCreditCard = ({ showPopupAddNewCreditCard, setShowPopupAddNewCreditCard, setIsRefresh }: CreditCardProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { themeStyles, theme } = useTheme();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const userId = Cookies.get("userId-jj-rental-ui");

    const [creditCardDetails, setCreditCardDetails] = useState<any>({
        cardholderName: "",
        creditCardNumber: "",
        expirationDate: "",
        ccvCode: "",
        ccLastFourDigit: ""
    });
    const textColor =
        theme === "dark"
            ? `${themeStyles.main.textSubHeadingColor}`
            : `${themeStyles.main.textHeadingColor}`;

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isProcessing) return;

        setIsProcessing(true);

        try {
            const lastFourDigits = creditCardDetails?.creditCardNumber?.slice(-4);

            const response = await axios.get(
                `${ticketBaseURL}groupCreditCard/DoesGroupCreditCardExist`,
                {
                    params: {
                        ccLastFourDigit: lastFourDigits,
                        tenantId: tenantId
                    }
                }
            );

            const cardExists = response.data === true;

            if (cardExists) {
                setConfirmData({
                    title: 'Warning',
                    summary: "This credit card is already added for this group.",
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);

                setIsProcessing(false);
                return;
            }

            await post_RdRentalCreditCard({
                ...creditCardDetails,
                isActive: true,
                userId: userId,
                tenantId: tenantId,
                cardZipCode: "0",
                ccLastFourDigit: lastFourDigits
            }, auth?.id);

            setIsRefresh(true);
            setShowPopupAddNewCreditCard(false);
            setCreditCardDetails({
                cardholderName: "",
                creditCardNumber: "",
                expirationDate: "",
                ccvCode: ""
            });
            handleTenantError(null);
        } catch (error) {
            handleTenantError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const closeModal = () => setShowPopupAddNewCreditCard(false);

    return (
        <Dialog
            open={showPopupAddNewCreditCard}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '800px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`fixed inset-0 p-4 mt-20 ${showPopupAddNewCreditCard ? 'flex' : 'hidden'} flex-wrap justify-center items-center w-full h-fit z-50 overflow-auto`}>
                <div className={`w-full max-w-[32rem] shadow-lg rounded-md p-5 relative ${themeStyles.main.tailwindMainBg}`}>
                    <Cross closeModal={closeModal} className={`w-3 h-3 absolute top-2 right-2 cursor-pointer ${textColor}`} />
                    <div style={{ width: "100%", marginTop: "1.25rem" }}>
                        <form
                            className="mt-1 flex flex-col gap-4"
                            onSubmit={handleSubmit}
                        >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Cardholder Name</label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={creditCardDetails.cardholderName}
                                    onChange={(e) =>
                                        setCreditCardDetails({
                                            ...creditCardDetails,
                                            cardholderName: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </div>

                            <div className="relative flex flex-col">
                                <CardNumberValidate
                                    value={creditCardDetails.creditCardNumber}
                                    setValue={(value) =>
                                        setCreditCardDetails({
                                            ...creditCardDetails,
                                            creditCardNumber: value,
                                        })
                                    }
                                />
                                <CreditCardIcon
                                    style={{
                                        width: "18px",
                                        height: "18px",
                                        position: "absolute",
                                        right: "1rem",
                                        top: "70%",
                                        transform: "translateY(-50%)",
                                        color: "#bbbbbb",
                                    }}
                                />
                            </div>

                            <div className="flex justify-between items-center gap-2">
                                <div className="relative flex-1 flex flex-col">
                                    <ExpiryDateValidator
                                        expiryDate={creditCardDetails.expirationDate}
                                        setExpiryDate={(value) =>
                                            setCreditCardDetails({
                                                ...creditCardDetails,
                                                expirationDate: value,
                                            })
                                        }
                                    />
                                </div>

                                <div className="relative flex-1 flex flex-col">
                                    <CvvValidate
                                        value={creditCardDetails.ccvCode}
                                        setValue={(value) =>
                                            setCreditCardDetails({
                                                ...creditCardDetails,
                                                ccvCode: value,
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <Button variant="contained"
                                    sx={{
                                        backgroundColor: colorConfigs.btn.main.bg
                                    }}
                                    type="submit"
                                >
                                    {isProcessing ? "Processing..." : "Save"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default AddCreditCard;